import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserPen } from 'lucide-react';

const FIELDS = [
  ['name', 'Name', 'text'], ['phone', 'Phone', 'tel'], ['email', 'Email', 'email'],
  ['from_house_number', 'From house no.', 'text'], ['from_postcode', 'From postcode', 'text'], ['from_address', 'From street, town', 'text'],
  ['to_house_number', 'To house no.', 'text'], ['to_postcode', 'To postcode', 'text'], ['to_address', 'To street, town', 'text'],
];

const Input = ({ k, label, type, value, onChange }) => (
  <label className={`block ${k.endsWith('address') || k === 'email' ? 'col-span-2' : ''}`}>
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(k, e.target.value)} className={`mt-1 w-full h-11 rounded border border-slate-200 bg-white px-2 text-base ${k.endsWith('postcode') ? 'uppercase' : ''}`} data-testid={`cust-edit-${k.replace(/_/g, '-')}`} />
  </label>
);

export const CustomerEditor = ({ item, authFetch, onSaved }) => {
  const initial = Object.fromEntries(FIELDS.map(([k]) => [k, item[k] || '']));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required.');
    setSaving(true);
    try {
      const { data } = await authFetch('patch', `/admin/estimate/${item.id}/customer`, { ...form, email: form.email.trim() || null, notify: notify && !!form.email.trim() });
      toast.success(data.emailed ? 'Details saved — customer emailed.' : 'Details saved.');
      setOpen(false);
      onSaved?.(data.estimate);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const field = Array.isArray(d) ? FIELDS.find(([k]) => d[0]?.loc?.includes(k)) : null;
      toast.error(field ? `Check the ${field[1].toLowerCase()}.` : (typeof d === 'string' ? d : "Couldn't save details."));
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => { setForm(initial); setOpen(true); }} className="inline-flex items-center gap-1.5 text-sm font-bold text-[color:var(--mv-orange)] h-11 px-3 -ml-3 rounded-md hover:bg-orange-50" data-testid={`cust-edit-open-${item.ref}`}>
        <UserPen className="w-3.5 h-3.5" /> Edit customer & addresses
      </button>
    );
  }
  return (
    <div className="rounded-md border border-orange-200 bg-orange-50 p-3 space-y-2" data-testid={`cust-edit-panel-${item.ref}`}>
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(([k, label, type]) => <Input key={k} k={k} label={label} type={type} value={form[k]} onChange={set} />)}
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} disabled={!form.email.trim()} className="w-4 h-4" data-testid="cust-edit-notify" />
        Email the customer their updated details
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="text-sm font-bold text-slate-500 h-11 px-4" data-testid="cust-edit-cancel">Close</button>
        <button type="button" onClick={save} disabled={saving} className="h-11 px-5 rounded-md bg-[color:var(--mv-navy)] text-white text-sm font-bold inline-flex items-center gap-1" data-testid="cust-edit-save">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
};
